/**
 * Infinity 3D Container Viewer
 * Three.js-based 3D viewer for the VSE Infinity skydiving container configurator
 *
 * Renders a colorable 3D model with per-panel zone materials.
 * Supports both GLB (primary) and legacy Three.js JSON (fallback) model formats.
 *
 * Usage:
 *   <canvas id="infinity-viewer-canvas"
 *     data-model-url="/path/to/infinity-model.glb"
 *     data-texture-base-url="/path/to/textures/">
 *   </canvas>
 *
 * Shopify Liquid injects URLs via data attributes:
 *   data-model-url="{{ 'infinity-model.glb' | asset_url }}"
 *   data-texture-base-url="{{ 'infinity-cordura-base.jpg' | asset_url | split: 'infinity-cordura-base.jpg' | first }}"
 *
 * @global window.InfinityViewer
 * @requires window.InfinityColors (from infinity-colors.js)
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';

/* ═══════════════════════════════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════════════════════════════ */

const MAX_PIXEL_RATIO = 2;
const DEFAULT_FOV = 45;
const NEAR_PLANE = 0.1;
const FAR_PLANE = 100;
const MIN_ZOOM_DISTANCE = 1.5;
const MAX_ZOOM_DISTANCE = 8;
const HIGHLIGHT_EMISSIVE_INTENSITY = 0.4;
const HIGHLIGHT_PULSE_SPEED = 2.0;

/**
 * Maps panel zone keys to material indices in the legacy model.
 * Indices correspond to the MeshFaceMaterial array order in Velocity's
 * infinity.js model (58 materials extracted from infinity.obj).
 *
 * Material index reference (from model DbgName):
 *   0: ContainerPanel1A    16: Metal             32: Logo
 *   1: ContainerPanel1B    17: Other             33-34: TopMudflap embroidery
 *   2: ContainerPanel1C    18: LegPads           35-36: Side1 text embroidery
 *   3: ContainerPanel2     19: Reserve           37-40: Side2 embroidery
 *   4: CenterFlapA         20: CutAway           41-44: LeftMudflap embroidery
 *   5: CenterFlapB         21: Black             45-46: Side1 symbol embroidery
 *   6: CenterFlapC         22: InnerLeg          47-54: RiserCover embroidery
 *   7: CenterFlapD         23-28: Piping_01-06   55: Hardware
 *   8: CenterFlapE         29: Risers            56: Panel2_XOut_Straps
 *   9: ContainerPanel3     30: Puck              57: Panel2_XOut
 *  10: ContainerPanel4     31: White
 *  11: ContainerPanel5
 *  12: BindingTape
 *  13: Webbing
 *  14: BackPad
 *  15: RingCover
 */
const ZONE_MATERIAL_MAP = {
  container_panel_1_color: { indices: [0, 1, 2], type: 'cordura' },
  container_panel_2_color: { indices: [3], type: 'cordura' },
  container_panel_3_color: { indices: [9], type: 'cordura' },
  container_panel_4_color: { indices: [10], type: 'cordura' },
  container_panel_5_color: { indices: [11], type: 'cordura' },
  container_centerflap_a_color: { indices: [4], type: 'cordura' },
  container_centerflap_b_color: { indices: [5], type: 'cordura' },
  container_centerflap_c_color: { indices: [6], type: 'cordura' },
  container_centerflap_d_color: { indices: [7], type: 'cordura' },
  container_centerflap_e_color: { indices: [8], type: 'cordura' },
  container_pinstripe_ab_color: { indices: [28], type: 'pinstripes' },
  container_pinstripe_bc_color: { indices: [27], type: 'pinstripes' },
  container_pinstripe_cd_color: { indices: [26], type: 'pinstripes' },
  container_pinstripe_de_color: { indices: [25], type: 'pinstripes' },
  container_backpad_color: { indices: [14], type: 'cordura' },
  container_bindingtape_color: { indices: [12], type: 'binding' },
  container_legpads_in_color: { indices: [22], type: 'cordura' },
  container_legpads_out_color: { indices: [18], type: 'cordura' },
  container_cutaway_color: { indices: [20], type: 'cordura' },
  container_mudflap_color: { indices: [15], type: 'cordura' },
  container_risers_color: { indices: [29], type: 'type17' },
  container_reserve_color: { indices: [19], type: 'cordura' },
  webbing_color: { indices: [13], type: 'type17' },
  // Party Stripe only (visible when pattern=partyStripe)
  design_party_stripe_color: { indices: [1], type: 'cordura' },
  design_party_pinstripe_1_color: { indices: [23], type: 'pinstripes' },
  design_party_pinstripe_2_color: { indices: [24], type: 'pinstripes' },
  // X-Out only (visible when pattern=xout)
  design_xout_color: { indices: [56], type: 'cordura' }
};

/**
 * Design-variant zones share geometry with base panel zones.
 * When a click lands on a design face, map it back to the base panel
 * so the configurator always selects the correct zone.
 */
const DESIGN_TO_BASE_MAP = {
  design_party_stripe_color:      'container_panel_1_color',
  design_party_pinstripe_1_color: 'container_pinstripe_ab_color',
  design_party_pinstripe_2_color: 'container_pinstripe_bc_color',
  design_xout_color:              'container_panel_1_color'
};

// Embroidery zones — each has fill (base) and outline (stitching) indices
const EMBROIDERY_ZONE_MAP = {
  embroidery_1flap_symbol_fill:       { indices: [33] },
  embroidery_1flap_symbol_out:        { indices: [34] },
  embroidery_ring_cover_text_fill:    { indices: [41] },
  embroidery_ring_cover_text_out:     { indices: [42] },
  embroidery_ring_cover_symbol_fill:  { indices: [43] },
  embroidery_ring_cover_symbol_out:   { indices: [44] },
  embroidery_sidebody_text_fill:      { indices: [35, 38] },
  embroidery_sidebody_text_out:       { indices: [36, 40] },
  embroidery_sidebody_symbol_fill:    { indices: [45, 37] },
  embroidery_sidebody_symbol_out:     { indices: [46, 39] },
  embroidery_riser_cover_text_fill:   { indices: [47, 49] },
  embroidery_riser_cover_text_out:    { indices: [53, 51] },
  embroidery_riser_cover_symbol_fill: { indices: [52, 48] },
  embroidery_riser_cover_symbol_out:  { indices: [54, 50] }
};

const HARDWARE_MATERIAL_INDICES = [16, 55];
const WEBBING_MATERIAL_INDICES = [13];
const BLACK_MATERIAL_INDICES = [21];
const RESERVE_MATERIAL_INDICES = [19];
const WHITE_MATERIAL_INDICES = [31];
const LOGO_MATERIAL_INDEX = 32;
const PUCK_MATERIAL_INDEX = 30;
const OTHER_MATERIAL_INDEX = 17;

const ALL_EMBROIDERY_INDICES = [
  33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46,
  47, 48, 49, 50, 51, 52, 53, 54
];

let currentPattern = 'standard';

/* ═══════════════════════════════════════════════════════════════════════════════
   VIEWER STATE
   ═══════════════════════════════════════════════════════════════════════════════ */

let canvas = null;
let renderer = null;
let scene = null;
let camera = null;
let controls = null;
let containerMesh = null;
let materials = [];
let textureLoader = null;
let clock = null;
let animationFrameId = null;
let isVisible = true;
let intersectionObserver = null;

// Textures
let corduraBaseTexture = null;
let corduraBumpTexture = null;
let meshBaseTexture = null;
let meshBumpTexture = null;
let camoTextures = {};

// Raycasting (click-to-select)
let raycaster = null;
const pointer = new THREE.Vector2();
let pointerDownPos = { x: 0, y: 0 };
let pointerDownTime = 0;
const CLICK_THRESHOLD = 5;       // px — movement below this = click, above = drag
const CLICK_TIME_LIMIT = 400;    // ms — held longer = drag
let reverseIndexMap = null;      // material index → zone key (built lazily)

// Highlight state
let highlightedZone = null;
let highlightedMaterials = [];
let highlightOriginalEmissive = [];
let highlightOriginalVisible = [];

// Configuration
let textureBaseUrl = '';
let modelUrl = '';

/* ═══════════════════════════════════════════════════════════════════════════════
   TEXTURE MANAGEMENT
   ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Load and cache the base Cordura fabric texture.
 * @returns {THREE.Texture}
 */
function loadCorduraBaseTexture() {
  if (corduraBaseTexture) return corduraBaseTexture;

  const url = textureBaseUrl + 'infinity-material-Cordura.jpg';
  corduraBaseTexture = textureLoader.load(url);
  corduraBaseTexture.wrapS = THREE.RepeatWrapping;
  corduraBaseTexture.wrapT = THREE.RepeatWrapping;
  corduraBaseTexture.repeat.set(4, 4);
  corduraBaseTexture.colorSpace = THREE.SRGBColorSpace;
  return corduraBaseTexture;
}

/**
 * Load and cache the Cordura bump/normal map texture.
 * @returns {THREE.Texture}
 */
function loadCorduraBumpTexture() {
  if (corduraBumpTexture) return corduraBumpTexture;

  const url = textureBaseUrl + 'infinity-bump-Cordura.jpg';
  corduraBumpTexture = textureLoader.load(url);
  corduraBumpTexture.wrapS = THREE.RepeatWrapping;
  corduraBumpTexture.wrapT = THREE.RepeatWrapping;
  corduraBumpTexture.repeat.set(4, 4);
  return corduraBumpTexture;
}

function loadMeshBaseTexture() {
  if (meshBaseTexture) return meshBaseTexture;
  const url = textureBaseUrl + 'infinity-material-Mesh.jpg';
  meshBaseTexture = textureLoader.load(url);
  meshBaseTexture.colorSpace = THREE.SRGBColorSpace;
  return meshBaseTexture;
}

function loadMeshBumpTexture() {
  if (meshBumpTexture) return meshBumpTexture;
  const url = textureBaseUrl + 'infinity-bump-Mesh.jpg';
  meshBumpTexture = textureLoader.load(url);
  return meshBumpTexture;
}

/**
 * Load a camo pattern texture by filename.
 * @param {string} filename
 * @returns {THREE.Texture}
 */
function loadCamoTexture(filename) {
  if (camoTextures[filename]) return camoTextures[filename];

  const url = textureBaseUrl + filename;
  const tex = textureLoader.load(url);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(2, 2);
  tex.colorSpace = THREE.SRGBColorSpace;
  camoTextures[filename] = tex;
  return tex;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   MATERIAL CREATION
   ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Create a standard Cordura fabric material.
 * @param {number} color - Hex color value
 * @returns {THREE.MeshStandardMaterial}
 */
function createCorduraMaterial(color = 0x333333) {
  return new THREE.MeshStandardMaterial({
    color: color,
    map: loadCorduraBaseTexture(),
    bumpMap: loadCorduraBumpTexture(),
    bumpScale: 0.3,
    roughness: 0.85,
    metalness: 0.0,
    side: THREE.DoubleSide
  });
}

/**
 * Create a binding tape material (smoother, slight sheen).
 * @param {number} color - Hex color value
 * @returns {THREE.MeshStandardMaterial}
 */
function createBindingMaterial(color = 0x000000) {
  const bindingTex = textureLoader.load(textureBaseUrl + 'infinity-material-Binding_Tape.jpg');
  bindingTex.wrapS = THREE.RepeatWrapping;
  bindingTex.wrapT = THREE.RepeatWrapping;
  bindingTex.repeat.set(8, 8);
  bindingTex.colorSpace = THREE.SRGBColorSpace;

  const bindingBump = textureLoader.load(textureBaseUrl + 'infinity-bump-Binding_Tape.jpg');
  bindingBump.wrapS = THREE.RepeatWrapping;
  bindingBump.wrapT = THREE.RepeatWrapping;
  bindingBump.repeat.set(8, 8);

  return new THREE.MeshStandardMaterial({
    color: color,
    map: bindingTex,
    bumpMap: bindingBump,
    bumpScale: 0.2,
    roughness: 0.6,
    metalness: 0.05,
    side: THREE.DoubleSide
  });
}

/**
 * Create a mesh/spacer material (backpad option).
 * @param {number} color - Hex color value
 * @returns {THREE.MeshStandardMaterial}
 */
function createMeshMaterial() {
  return new THREE.MeshStandardMaterial({
    color: 0xffffff,
    map: loadMeshBaseTexture(),
    bumpMap: loadMeshBumpTexture(),
    bumpScale: 0.4,
    roughness: 0.95,
    metalness: 0.0,
    side: THREE.DoubleSide
  });
}

/**
 * Create a hardware material (stainless or black).
 * @param {string} type - 'stainless' or 'black'
 * @returns {THREE.MeshStandardMaterial}
 */
function createHardwareMaterial(type = 'stainless') {
  if (type === 'black') {
    return new THREE.MeshStandardMaterial({
      color: 0x1a1a1a,
      roughness: 0.3,
      metalness: 0.9,
      envMapIntensity: 0.8
    });
  }
  return new THREE.MeshStandardMaterial({
    color: 0xcccccc,
    roughness: 0.2,
    metalness: 0.95,
    envMapIntensity: 1.0
  });
}

/**
 * Create a webbing material.
 * @param {number} color - Hex color value
 * @returns {THREE.MeshStandardMaterial}
 */
function createWebbingMaterial(color = 0x000000) {
  return new THREE.MeshStandardMaterial({
    color: color,
    roughness: 0.75,
    metalness: 0.0,
    side: THREE.DoubleSide
  });
}

/**
 * Create an embroidery material.
 * @param {number} color - Hex color value
 * @returns {THREE.MeshStandardMaterial}
 */
function createEmbroideryMaterial(color = 0xffffff) {
  return new THREE.MeshStandardMaterial({
    color: color,
    roughness: 0.45,
    metalness: 0.0,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
    depthWrite: false,
    emissive: color,
    emissiveIntensity: 0.15
  });
}

/**
 * Build the complete material array (58 materials) in the order the legacy
 * model references them via face materialIndex.
 * @returns {THREE.MeshStandardMaterial[]}
 */
function buildMaterialArray() {
  const mats = new Array(58).fill(null);

  // Assign zone materials (colorable panels)
  for (const zoneKey of Object.keys(ZONE_MATERIAL_MAP)) {
    const zoneInfo = ZONE_MATERIAL_MAP[zoneKey];
    const mat = zoneInfo.type === 'binding'
      ? createBindingMaterial(0x333333)
      : zoneInfo.type === 'type17'
        ? createWebbingMaterial(0x333333)
        : createCorduraMaterial(0x333333);
    mat.userData = { zoneKey, type: zoneInfo.type };

    for (const idx of zoneInfo.indices) {
      mats[idx] = mat;
    }
  }

  // Hardware (Metal index 16, Hardware index 55)
  const hwMat = createHardwareMaterial('stainless');
  hwMat.userData = { type: 'hardware' };
  for (const idx of HARDWARE_MATERIAL_INDICES) {
    mats[idx] = hwMat;
  }

  // Permanently black elements (index 21)
  const blackMat = new THREE.MeshStandardMaterial({
    color: 0x000000, roughness: 0.8, metalness: 0.0, side: THREE.DoubleSide
  });
  blackMat.userData = { type: 'black' };
  for (const idx of BLACK_MATERIAL_INDICES) {
    mats[idx] = blackMat;
  }

  // Reserve (index 19) — internal, not user-colorable
  const reserveMat = createCorduraMaterial(0x222222);
  reserveMat.userData = { type: 'reserve' };
  for (const idx of RESERVE_MATERIAL_INDICES) {
    mats[idx] = reserveMat;
  }

  // White elements (index 31)
  const whiteMat = new THREE.MeshStandardMaterial({
    color: 0xffffff, roughness: 0.7, metalness: 0.0, side: THREE.DoubleSide
  });
  whiteMat.userData = { type: 'white' };
  for (const idx of WHITE_MATERIAL_INDICES) {
    mats[idx] = whiteMat;
  }

  // Logo (index 32)
  const logoMat = new THREE.MeshStandardMaterial({
    color: 0xffffff, roughness: 0.5, metalness: 0.1, side: THREE.DoubleSide
  });
  logoMat.userData = { type: 'logo' };
  mats[LOGO_MATERIAL_INDEX] = logoMat;

  // Puck / pilot chute handle (index 30)
  const puckMat = new THREE.MeshStandardMaterial({
    color: 0x333333, roughness: 0.6, metalness: 0.2, side: THREE.DoubleSide
  });
  puckMat.userData = { type: 'puck' };
  mats[PUCK_MATERIAL_INDEX] = puckMat;

  // Other (index 17) — miscellaneous panels
  const otherMat = createCorduraMaterial(0x222222);
  otherMat.userData = { type: 'other' };
  mats[OTHER_MATERIAL_INDEX] = otherMat;

  // X-Out pattern (indices 56-57) — hidden by default (standard pattern)
  const xoutMat = createCorduraMaterial(0x333333);
  xoutMat.userData = { zoneKey: 'design_xout_color', type: 'cordura' };
  xoutMat.visible = false;
  mats[56] = xoutMat;
  const xoutBaseMat = createCorduraMaterial(0x333333);
  xoutBaseMat.userData = { type: 'xout_base' };
  xoutBaseMat.visible = false;
  mats[57] = xoutBaseMat;

  // Party Stripe piping (indices 23-24) — hidden by default
  for (const idx of [23, 24]) {
    if (mats[idx]) mats[idx].visible = false;
  }

  // Embroidery zones (indices 33-54)
  for (const idx of ALL_EMBROIDERY_INDICES) {
    if (!mats[idx]) {
      const embMat = createEmbroideryMaterial(0xffffff);
      embMat.userData = { type: 'embroidery' };
      mats[idx] = embMat;
    }
  }

  // Fill any remaining nulls
  for (let i = 0; i < mats.length; i++) {
    if (!mats[i]) {
      mats[i] = createCorduraMaterial(0x333333);
      mats[i].userData = { type: 'default' };
    }
  }

  return mats;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   LEGACY JSON LOADER
   Parses the old Three.js r60-era JSON geometry format used by
   design.velocityrigs.com/assets/infinity.js
   ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Parse legacy Three.js JSON format into BufferGeometry + material groups.
 *
 * Legacy format structure:
 * {
 *   vertices: [x,y,z, x,y,z, ...],      // flat array, 35472 vertices
 *   faces: [type, v0, v1, v2, ..., materialIndex, ...],  // bitfield encoded
 *   materials: [{DbgName, ...}, ...]     // 58 materials
 * }
 *
 * Face type bitfield:
 *   bit 0 = isQuad
 *   bit 1 = hasMaterial
 *   bit 2 = hasFaceUV
 *   bit 3 = hasFaceVertexUV
 *   bit 4 = hasFaceNormal
 *   bit 5 = hasFaceVertexNormal
 *   bit 6 = hasFaceColor
 *   bit 7 = hasFaceVertexColor
 *
 * @param {object} json - Parsed JSON model data
 * @returns {THREE.Mesh}
 */
function parseLegacyJSON(json) {
  const vertices = json.vertices || [];
  const faces = json.faces || [];
  const uvs = json.uvs || [];
  const normals = json.normals || [];

  // Collect triangulated faces with material indices
  const positionArray = [];
  const normalArray = [];
  const uvArray = [];
  const materialIndexPerFace = [];

  let offset = 0;

  while (offset < faces.length) {
    const type = faces[offset++];

    const isQuad = (type & 1) !== 0;
    const hasMaterial = (type & 2) !== 0;
    const hasFaceUV = (type & 4) !== 0;
    const hasFaceVertexUV = (type & 8) !== 0;
    const hasFaceNormal = (type & 16) !== 0;
    const hasFaceVertexNormal = (type & 32) !== 0;
    const hasFaceColor = (type & 64) !== 0;
    const hasFaceVertexColor = (type & 128) !== 0;

    const numVertices = isQuad ? 4 : 3;

    // Read vertex indices
    const vertIndices = [];
    for (let i = 0; i < numVertices; i++) {
      vertIndices.push(faces[offset++]);
    }

    // Read material index
    let materialIndex = 0;
    if (hasMaterial) {
      materialIndex = faces[offset++];
    }

    // Read face UV (deprecated, skip)
    if (hasFaceUV) {
      offset++; // skip face UV index
    }

    // Read face vertex UVs
    const faceVertUVs = [];
    if (hasFaceVertexUV) {
      const uvLayer = uvs[0] || [];
      for (let i = 0; i < numVertices; i++) {
        const uvIndex = faces[offset++];
        if (uvLayer.length > 0) {
          faceVertUVs.push(uvLayer[uvIndex * 2], uvLayer[uvIndex * 2 + 1]);
        } else {
          faceVertUVs.push(0, 0);
        }
      }
    }

    // Read face normal (single normal for the face)
    if (hasFaceNormal) {
      offset++; // skip face normal index
    }

    // Read face vertex normals
    const faceVertNormals = [];
    if (hasFaceVertexNormal) {
      for (let i = 0; i < numVertices; i++) {
        const nIndex = faces[offset++];
        faceVertNormals.push(
          normals[nIndex * 3],
          normals[nIndex * 3 + 1],
          normals[nIndex * 3 + 2]
        );
      }
    }

    // Skip face color
    if (hasFaceColor) {
      offset++;
    }

    // Skip vertex colors
    if (hasFaceVertexColor) {
      offset += numVertices;
    }

    // Triangulate: emit 1 triangle for tri, 2 for quad
    const triSets = isQuad
      ? [[0, 1, 2], [0, 2, 3]]
      : [[0, 1, 2]];

    for (const [a, b, c] of triSets) {
      // Positions
      for (const vi of [a, b, c]) {
        const idx = vertIndices[vi];
        positionArray.push(
          vertices[idx * 3],
          vertices[idx * 3 + 1],
          vertices[idx * 3 + 2]
        );
      }

      // Normals
      if (faceVertNormals.length > 0) {
        for (const vi of [a, b, c]) {
          normalArray.push(
            faceVertNormals[vi * 3],
            faceVertNormals[vi * 3 + 1],
            faceVertNormals[vi * 3 + 2]
          );
        }
      }

      // UVs
      if (faceVertUVs.length > 0) {
        for (const vi of [a, b, c]) {
          uvArray.push(faceVertUVs[vi * 2], faceVertUVs[vi * 2 + 1]);
        }
      }

      materialIndexPerFace.push(materialIndex);
    }
  }

  // Build BufferGeometry
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positionArray, 3));

  if (normalArray.length > 0) {
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normalArray, 3));
  } else {
    geometry.computeVertexNormals();
  }

  if (uvArray.length > 0) {
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvArray, 2));
  }

  // Sort faces by material index and build groups for multi-material rendering
  // We need to reorder the triangles so each material's faces are contiguous
  const triCount = materialIndexPerFace.length;
  const sortedIndices = Array.from({ length: triCount }, (_, i) => i);
  sortedIndices.sort((a, b) => materialIndexPerFace[a] - materialIndexPerFace[b]);

  // Rebuild arrays in sorted order
  const sortedPositions = new Float32Array(triCount * 9);
  const sortedNormals = normalArray.length > 0 ? new Float32Array(triCount * 9) : null;
  const sortedUVs = uvArray.length > 0 ? new Float32Array(triCount * 6) : null;

  for (let i = 0; i < triCount; i++) {
    const srcTri = sortedIndices[i];

    // Copy 3 vertices (9 floats for position/normal, 6 for UV)
    for (let v = 0; v < 9; v++) {
      sortedPositions[i * 9 + v] = positionArray[srcTri * 9 + v];
      if (sortedNormals) {
        sortedNormals[i * 9 + v] = normalArray[srcTri * 9 + v];
      }
    }
    if (sortedUVs) {
      for (let v = 0; v < 6; v++) {
        sortedUVs[i * 6 + v] = uvArray[srcTri * 6 + v];
      }
    }
  }

  // Replace geometry attributes with sorted data
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(sortedPositions, 3));
  if (sortedNormals) {
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(sortedNormals, 3));
  }
  if (sortedUVs) {
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(sortedUVs, 2));
  }

  // Build material groups
  const sortedMaterialIndices = sortedIndices.map(i => materialIndexPerFace[i]);
  let groupStart = 0;
  let currentMat = sortedMaterialIndices[0];

  for (let i = 1; i <= triCount; i++) {
    if (i === triCount || sortedMaterialIndices[i] !== currentMat) {
      geometry.addGroup(groupStart * 3, (i - groupStart) * 3, currentMat);
      if (i < triCount) {
        groupStart = i;
        currentMat = sortedMaterialIndices[i];
      }
    }
  }

  // Create mesh with material array
  const mesh = new THREE.Mesh(geometry, materials);
  return mesh;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   GLB LOADER
   Primary loader path — expects a GLB with named mesh parts or material slots
   ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Load GLB model and map its materials to our zone system.
 * @param {string} url - URL to the .glb file
 * @returns {Promise<THREE.Group>}
 */
function loadGLBModel(url) {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader();

    // Set up Draco decoder for compressed meshes
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/libs/draco/');
    loader.setDRACOLoader(dracoLoader);

    loader.load(
      url,
      (gltf) => {
        const model = gltf.scene;

        // Try to map materials by name or index
        model.traverse((child) => {
          if (child.isMesh) {
            // If the mesh has multiple material groups, map them
            if (Array.isArray(child.material)) {
              child.material = child.material.map((mat, idx) => {
                if (idx < materials.length) {
                  return materials[idx];
                }
                return mat;
              });
            } else {
              // Single material — try to find zone by mesh name
              const zoneName = findZoneByMeshName(child.name);
              if (zoneName) {
                const zoneInfo = ZONE_MATERIAL_MAP[zoneName];
                if (zoneInfo && zoneInfo.indices.length > 0) {
                  child.material = materials[zoneInfo.indices[0]];
                }
              }
            }
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });

        dracoLoader.dispose();
        resolve(model);
      },
      undefined,
      (error) => {
        dracoLoader.dispose();
        reject(error);
      }
    );
  });
}

/**
 * Try to match a mesh name from the GLB to a panel zone key.
 * @param {string} meshName
 * @returns {string|null}
 */
function findZoneByMeshName(meshName) {
  if (!meshName) return null;
  const lower = meshName.toLowerCase().replace(/[^a-z0-9]/g, '_');

  for (const zoneKey of Object.keys(ZONE_MATERIAL_MAP)) {
    const zoneShort = zoneKey.replace('container_', '').replace('_color', '');
    if (lower.includes(zoneShort)) {
      return zoneKey;
    }
  }
  return null;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   SCENE SETUP
   ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Set up the lighting rig for the 3D scene.
 */
function setupLighting() {
  // Ambient fill light
  const ambient = new THREE.AmbientLight(0xffffff, 0.5);
  scene.add(ambient);

  // Key light (upper-right, slightly warm)
  const keyLight = new THREE.DirectionalLight(0xfff8f0, 1.0);
  keyLight.position.set(3, 5, 4);
  keyLight.castShadow = false;
  scene.add(keyLight);

  // Fill light (left side, cooler)
  const fillLight = new THREE.DirectionalLight(0xf0f4ff, 0.6);
  fillLight.position.set(-3, 3, -2);
  scene.add(fillLight);

  // Subtle rim/back light
  const rimLight = new THREE.DirectionalLight(0xffffff, 0.3);
  rimLight.position.set(0, 2, -5);
  scene.add(rimLight);

  // Environment map for metallic hardware reflections
  const pmremGenerator = new THREE.PMREMGenerator(renderer);
  pmremGenerator.compileEquirectangularShader();

  // Create a simple neutral environment
  const envScene = new THREE.Scene();
  envScene.background = new THREE.Color(0x888888);

  // Add gradient lights to env scene for soft reflections
  const envLight1 = new THREE.Mesh(
    new THREE.PlaneGeometry(10, 10),
    new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide })
  );
  envLight1.position.set(0, 5, 0);
  envLight1.rotation.x = Math.PI / 2;
  envScene.add(envLight1);

  const envTexture = pmremGenerator.fromScene(envScene, 0.04).texture;
  scene.environment = envTexture;
  pmremGenerator.dispose();
}

/**
 * Set up camera with default position.
 * Angled front-quarter view showing the back of the container.
 */
function setupCamera() {
  const aspect = canvas.clientWidth / canvas.clientHeight;
  camera = new THREE.PerspectiveCamera(DEFAULT_FOV, aspect, NEAR_PLANE, FAR_PLANE);

  // Position: angled rear-quarter view
  camera.position.set(-2.5, 1.8, 3.5);
  camera.lookAt(0, 0.5, 0);
}

/**
 * Set up OrbitControls with constrained interaction.
 */
function setupControls() {
  controls = new OrbitControls(camera, canvas);

  // Target: center of container model
  controls.target.set(0, 0.5, 0);

  // Smooth damping
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;

  // Zoom constraints
  controls.minDistance = MIN_ZOOM_DISTANCE;
  controls.maxDistance = MAX_ZOOM_DISTANCE;

  // Prevent flipping upside down
  controls.minPolarAngle = 0.2; // Slightly above horizon
  controls.maxPolarAngle = Math.PI - 0.2; // Slightly below bottom

  // Enable touch
  controls.enablePan = true;
  controls.panSpeed = 0.5;
  controls.enableZoom = true;
  controls.zoomSpeed = 1.0;

  // Limit panning distance
  controls.maxPolarAngle = Math.PI * 0.85;

  controls.update();
}

/* ═══════════════════════════════════════════════════════════════════════════════
   COLOR APPLICATION
   ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Resolve a color input to a hex number.
 * Accepts: hex number (0xff0000), hex string ('#ff0000'), or color name ('Red').
 *
 * @param {number|string} colorInput
 * @returns {number|null} Hex color value or null if not found
 */
function resolveColor(colorInput) {
  // Already a number (hex value)
  if (typeof colorInput === 'number') {
    return colorInput;
  }

  if (typeof colorInput !== 'string') return null;

  // Hex string format
  if (colorInput.startsWith('#')) {
    return parseInt(colorInput.slice(1), 16);
  }

  // If starts with '0x'
  if (colorInput.startsWith('0x')) {
    return parseInt(colorInput, 16);
  }

  // Look up by name in InfinityColors
  const colors = window.InfinityColors;
  if (!colors) return null;

  // Check cordura palette first
  if (colors.cordura && colors.cordura[colorInput]) {
    return colors.cordura[colorInput].fill;
  }

  // Check binding tape palette
  if (colors.bindingTape && colors.bindingTape[colorInput]) {
    return colors.bindingTape[colorInput].fill;
  }

  // Check type17 (risers/webbing) palette
  if (colors.type17 && colors.type17[colorInput]) {
    return colors.type17[colorInput].fill;
  }

  // Check binding tape stitching palette
  if (colors.bindingTapeStitching && colors.bindingTapeStitching[colorInput]) {
    return colors.bindingTapeStitching[colorInput].fill;
  }

  return null;
}

/**
 * Check if a color name is a camo pattern.
 * @param {string} colorInput
 * @returns {object|null} Camo data or null
 */
function getCamoData(colorInput) {
  if (typeof colorInput !== 'string') return null;
  const colors = window.InfinityColors;
  if (!colors || !colors.cordura) return null;

  const colorData = colors.cordura[colorInput];
  if (colorData && colorData.camo && colorData.texture) {
    return colorData;
  }
  return null;
}

/**
 * Apply a color to all materials belonging to a specific zone.
 * @param {string} zoneName - Zone key (e.g., 'container_panel_1_color')
 * @param {number|string} colorInput - Hex value, hex string, or color name
 */
function applyZoneColor(zoneName, colorInput) {
  const zoneInfo = ZONE_MATERIAL_MAP[zoneName];
  if (!zoneInfo) return;

  // Handle 'None' for binding zones (make transparent/invisible)
  if (colorInput === 'None' || colorInput === null) {
    for (const idx of zoneInfo.indices) {
      if (materials[idx]) {
        materials[idx].visible = false;
      }
    }
    return;
  }

  const camoData = getCamoData(colorInput);
  const hexColor = resolveColor(colorInput);

  if (hexColor === null && !camoData) return;

  for (const idx of zoneInfo.indices) {
    if (!materials[idx]) continue;

    materials[idx].visible = true;

    if (camoData) {
      // Camo pattern: swap the diffuse map
      const camoTex = loadCamoTexture(camoData.texture);
      materials[idx].map = camoTex;
      materials[idx].color.setHex(0xffffff); // White tint so texture shows true color
      materials[idx].bumpMap = loadCorduraBumpTexture();
      materials[idx].needsUpdate = true;
    } else {
      // Solid color: check if material is mesh type (backpad)
      const isMesh = materials[idx].userData && materials[idx].userData.type === 'mesh';
      if (isMesh) {
        materials[idx].map = loadMeshBaseTexture();
        materials[idx].bumpMap = loadMeshBumpTexture();
        materials[idx].color.setHex(0xffffff);
      } else if (zoneInfo.type === 'cordura') {
        materials[idx].map = loadCorduraBaseTexture();
        materials[idx].bumpMap = loadCorduraBumpTexture();
        materials[idx].color.setHex(hexColor);
      } else {
        materials[idx].map = null;
        materials[idx].bumpMap = null;
        materials[idx].color.setHex(hexColor);
      }
      materials[idx].needsUpdate = true;
    }
  }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PATTERN SWITCHING (Standard / Party Stripe / X-Out)
   ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Switch between Standard, Party Stripe, and X-Out patterns.
 * Matches Velocity's design_configuration: 0=standard, 1=partyStripe, 2=xout
 *
 * @param {string} pattern - 'standard', 'partyStripe', or 'xout'
 * @param {object} colors - { panel1Color, stripeColor, pinstripe1Color, pinstripe2Color, xoutColor }
 */
function applyPattern(pattern, colors = {}) {
  currentPattern = pattern;
  const panel1Color = colors.panel1Color || 'Red';

  if (pattern === 'partyStripe') {
    // Panel1A + 1C get panel_1_color; Panel1B gets stripe color
    const stripeColor = colors.stripeColor || 'White';
    const pin1 = colors.pinstripe1Color || 'None';
    const pin2 = colors.pinstripe2Color || 'None';

    applyZoneColorDirect([0, 2], 'cordura', panel1Color);
    applyZoneColorDirect([1], 'cordura', stripeColor);
    applyZoneColorDirect([23], 'pinstripes', pin1);
    applyZoneColorDirect([24], 'pinstripes', pin2);
    // Hide X-Out
    setIndicesVisible([56, 57], false);

  } else if (pattern === 'xout') {
    // Panel2_XOut gets panel_1_color; Panel2_XOut_Straps gets xout color
    const xoutColor = colors.xoutColor || 'White';

    // Hide Panel1 A/B/C and party stripe piping
    setIndicesVisible([0, 1, 2], false);
    setIndicesVisible([23, 24], false);
    // Show X-Out
    applyZoneColorDirect([57], 'cordura', panel1Color);
    applyZoneColorDirect([56], 'cordura', xoutColor);

  } else {
    // Standard: Panel1A/B/C all get panel_1_color
    applyZoneColorDirect([0, 1, 2], 'cordura', panel1Color);
    // Hide party stripe piping and X-Out
    setIndicesVisible([23, 24], false);
    setIndicesVisible([56, 57], false);
  }
}

function applyZoneColorDirect(indices, type, colorInput) {
  if (colorInput === 'None' || colorInput === null) {
    setIndicesVisible(indices, false);
    return;
  }

  const camoData = getCamoData(colorInput);
  const hexColor = resolveColor(colorInput);
  if (hexColor === null && !camoData) return;

  for (const idx of indices) {
    if (!materials[idx]) continue;
    materials[idx].visible = true;

    if (camoData) {
      const camoTex = loadCamoTexture(camoData.texture);
      materials[idx].map = camoTex;
      materials[idx].color.setHex(0xffffff);
      materials[idx].bumpMap = loadCorduraBumpTexture();
    } else {
      materials[idx].color.setHex(hexColor);
      if (type === 'cordura') {
        materials[idx].map = loadCorduraBaseTexture();
        materials[idx].bumpMap = loadCorduraBumpTexture();
      }
    }
    materials[idx].needsUpdate = true;
  }
}

function setIndicesVisible(indices, visible) {
  for (const idx of indices) {
    if (materials[idx]) materials[idx].visible = visible;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   EMBROIDERY COLORS
   ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Apply a color to an embroidery zone.
 * @param {string} zoneKey - e.g. 'embroidery_1flap_symbol_fill'
 * @param {number|string} colorInput - Hex value, hex string, or color name
 */
function applyEmbroideryColor(zoneKey, colorInput) {
  const zone = EMBROIDERY_ZONE_MAP[zoneKey];
  if (!zone) return;

  if (colorInput === 'None' || colorInput === null) {
    for (const idx of zone.indices) {
      if (materials[idx]) materials[idx].visible = false;
    }
    return;
  }

  const hexColor = resolveColor(colorInput);
  if (hexColor === null) return;

  for (const idx of zone.indices) {
    if (!materials[idx]) continue;
    materials[idx].visible = true;
    materials[idx].color.setHex(hexColor);
    if (materials[idx].emissive) materials[idx].emissive.setHex(hexColor);
    materials[idx].emissiveIntensity = 0.15;
    materials[idx].needsUpdate = true;
  }
}

/**
 * Show neutral embroidery preview geometry without changing the customer's
 * saved embroidery config/order payload. Real selected thread colors still
 * override these placeholders; selecting None still hides that field.
 */
function showEmbroideryPlaceholders() {
  for (const [zoneKey, zone] of Object.entries(EMBROIDERY_ZONE_MAP)) {
    const placeholderColor = zoneKey.endsWith('_out') ? 0xffffff : 0x111111;
    for (const idx of zone.indices) {
      if (!materials[idx]) continue;
      materials[idx].visible = true;
      materials[idx].color.setHex(placeholderColor);
      if (materials[idx].emissive) materials[idx].emissive.setHex(placeholderColor);
      materials[idx].emissiveIntensity = 0.18;
      materials[idx].needsUpdate = true;
    }
  }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   CLICK-TO-SELECT (RAYCASTING)
   ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Build a reverse lookup: material index → zone key.
 * Called once after materials are set up.
 */
function buildReverseIndexMap() {
  reverseIndexMap = {};

  // Design-variant zones (party stripe, x-out) share material indices with
  // base panel zones.  Map them first so that the base zones overwrite them
  // and always win — clicks on shared geometry should select the base panel.
  const designPrefixes = ['design_'];
  const baseKeys = [];
  const designKeys = [];
  for (const key of Object.keys(ZONE_MATERIAL_MAP)) {
    if (designPrefixes.some(p => key.startsWith(p))) {
      designKeys.push(key);
    } else {
      baseKeys.push(key);
    }
  }

  // 1. Design variants first (will be overwritten by base zones)
  for (const key of designKeys) {
    for (const idx of ZONE_MATERIAL_MAP[key].indices) {
      reverseIndexMap[idx] = key;
    }
  }
  // 2. Base zones overwrite any shared indices
  for (const key of baseKeys) {
    for (const idx of ZONE_MATERIAL_MAP[key].indices) {
      reverseIndexMap[idx] = key;
    }
  }

  // 3. Embroidery indices — only fill gaps
  for (const [zoneKey, zoneInfo] of Object.entries(EMBROIDERY_ZONE_MAP)) {
    for (const idx of zoneInfo.indices) {
      if (!reverseIndexMap[idx]) {
        reverseIndexMap[idx] = zoneKey;
      }
    }
  }
}

/**
 * Given a canvas-space (x, y), raycast into the scene and return the
 * zone key of the hit face's material, or null.
 * @param {number} clientX
 * @param {number} clientY
 * @returns {string|null}
 */
function raycastZone(clientX, clientY) {
  if (!raycaster || !camera || !containerMesh) return null;
  if (!reverseIndexMap) buildReverseIndexMap();

  const rect = canvas.getBoundingClientRect();
  pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
  pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;

  raycaster.setFromCamera(pointer, camera);

  // For legacy format the mesh has an array of materials; face.materialIndex
  // tells us which one was hit.
  const targets = containerMesh.isMesh ? [containerMesh] : [];
  if (!containerMesh.isMesh) {
    containerMesh.traverse(child => { if (child.isMesh) targets.push(child); });
  }

  const hits = raycaster.intersectObjects(targets, false);
  if (hits.length === 0) return null;

  const hit = hits[0];

  let zone = null;

  // Legacy format: single mesh with material array
  if (hit.face && hit.face.materialIndex !== undefined) {
    const matIdx = hit.face.materialIndex;
    zone = reverseIndexMap[matIdx] || null;
  }

  // GLB format: each child mesh has a single material with userData.zoneKey
  if (!zone && hit.object && hit.object.material && hit.object.material.userData) {
    zone = hit.object.material.userData.zoneKey || null;
  }

  // Map design-variant zones back to their base panel
  return DESIGN_TO_BASE_MAP[zone] || zone;
}

/**
 * Pointer-down handler — record start position and time.
 * Registered in capture phase so it fires before OrbitControls.
 * @param {PointerEvent} e
 */
function onPointerDown(e) {
  pointerDownPos.x = e.clientX;
  pointerDownPos.y = e.clientY;
  pointerDownTime = performance.now();
}

/**
 * Click handler — if the pointer barely moved (click, not drag),
 * perform a raycast and dispatch a zone-select event.
 *
 * Uses the native 'click' event which fires after OrbitControls finishes
 * processing pointer events, avoiding all interference with drag/rotate.
 * @param {MouseEvent} e
 */
function onClick(e) {
  const dx = e.clientX - pointerDownPos.x;
  const dy = e.clientY - pointerDownPos.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const elapsed = performance.now() - pointerDownTime;

  // Only treat as click if the pointer barely moved and was quick
  if (dist > CLICK_THRESHOLD || elapsed > CLICK_TIME_LIMIT) return;

  const zoneKey = raycastZone(e.clientX, e.clientY);

  // Dispatch custom event so the configurator can react.
  // A null zone means the user clicked empty canvas space and wants to clear selection.
  canvas.dispatchEvent(new CustomEvent('infinity-viewer:zone-clicked', {
    bubbles: true,
    detail: { zoneKey }
  }));
}

/**
 * Pointer-move handler — change cursor when hovering over a clickable zone.
 * Throttled to one raycast per animation frame.
 * @param {PointerEvent} e
 */
let hoverRafId = null;
function onPointerMove(e) {
  if (hoverRafId) return;
  hoverRafId = requestAnimationFrame(() => {
    hoverRafId = null;
    if (!canvas) return;
    const zone = raycastZone(e.clientX, e.clientY);
    canvas.style.cursor = zone ? 'pointer' : 'grab';
  });
}

/**
 * Attach click-to-select event listeners to the canvas.
 *
 * pointerdown uses capture phase to record position before OrbitControls.
 * click fires natively after pointer events, immune to OrbitControls interference.
 */
function setupClickToSelect() {
  raycaster = new THREE.Raycaster();
  canvas.addEventListener('pointerdown', onPointerDown, { capture: true });
  canvas.addEventListener('click', onClick);
  canvas.addEventListener('pointermove', onPointerMove);
}

/* ═══════════════════════════════════════════════════════════════════════════════
   HIGHLIGHT / PULSE EFFECT
   ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Start highlighting a zone with emissive glow.
 * @param {string} zoneName
 */
function startHighlight(zoneName) {
  clearHighlightState();

  const zoneInfo = ZONE_MATERIAL_MAP[zoneName];
  if (!zoneInfo) return;

  highlightedZone = zoneName;
  highlightedMaterials = [];
  highlightOriginalEmissive = [];
  highlightOriginalVisible = [];

  for (const idx of zoneInfo.indices) {
    if (materials[idx]) {
      highlightedMaterials.push(materials[idx]);
      highlightOriginalEmissive.push(materials[idx].emissive.clone());
      highlightOriginalVisible.push(materials[idx].visible);
      materials[idx].visible = true;
      materials[idx].emissiveIntensity = HIGHLIGHT_EMISSIVE_INTENSITY;
    }
  }
}

/**
 * Clear current highlight state.
 */
function clearHighlightState() {
  for (let i = 0; i < highlightedMaterials.length; i++) {
    const mat = highlightedMaterials[i];
    if (mat && highlightOriginalEmissive[i]) {
      mat.emissive.copy(highlightOriginalEmissive[i]);
      mat.emissiveIntensity = 0;
    }
    if (mat && highlightOriginalVisible[i] !== undefined) {
      mat.visible = highlightOriginalVisible[i];
    }
  }
  highlightedZone = null;
  highlightedMaterials = [];
  highlightOriginalEmissive = [];
  highlightOriginalVisible = [];
}

/**
 * Update highlight pulse effect (called each frame).
 * @param {number} elapsed - Total elapsed time in seconds
 */
function updateHighlight(elapsed) {
  if (!highlightedZone || highlightedMaterials.length === 0) return;

  // Pulsing emissive: oscillate between 0.2 and 0.6
  const pulse = 0.2 + 0.4 * (0.5 + 0.5 * Math.sin(elapsed * HIGHLIGHT_PULSE_SPEED * Math.PI));

  for (const mat of highlightedMaterials) {
    mat.emissive.setHex(0xff2222);
    mat.emissiveIntensity = pulse;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   RENDER LOOP & VISIBILITY
   ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Main animation loop.
 */
function animate() {
  animationFrameId = requestAnimationFrame(animate);

  if (!isVisible) return;

  const elapsed = clock.getElapsedTime();

  // Update orbit controls damping
  controls.update();

  // Update highlight pulse
  updateHighlight(elapsed);

  // Render
  renderer.render(scene, camera);
}

/**
 * Set up IntersectionObserver to pause rendering when viewer is off-screen.
 */
function setupVisibilityObserver() {
  if (!('IntersectionObserver' in window)) return;

  intersectionObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        isVisible = entry.isIntersecting;
      }
    },
    { threshold: 0.05 }
  );

  intersectionObserver.observe(canvas);
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PUBLIC API
   ═══════════════════════════════════════════════════════════════════════════════ */

const InfinityViewer = {

  /**
   * Initialize the 3D viewer.
   * @param {HTMLCanvasElement} canvasElement - The canvas to render into
   * @param {object} [options] - Configuration options
   * @param {string} [options.modelUrl] - URL to the model file (.glb or .json)
   * @param {string} [options.textureBaseUrl] - Base URL for texture assets
   * @param {string} [options.modelFormat] - 'glb' or 'json' (auto-detected from URL)
   * @param {object} [options.initialConfig] - Initial color configuration object
   */
  async init(canvasElement, options = {}) {
    canvas = canvasElement;

    // Read URLs from data attributes or options
    modelUrl = options.modelUrl
      || canvas.dataset.modelUrl
      || '/assets/infinity-model.glb';

    textureBaseUrl = options.textureBaseUrl
      || canvas.dataset.textureBaseUrl
      || '/assets/';

    // Ensure textureBaseUrl ends with /
    if (!textureBaseUrl.endsWith('/')) {
      textureBaseUrl += '/';
    }

    // Create texture loader
    textureLoader = new THREE.TextureLoader();

    // Create renderer
    renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO));
    renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    // Create scene
    scene = new THREE.Scene();
    scene.background = null; // Transparent — CSS handles background

    // Create clock
    clock = new THREE.Clock();

    // Setup camera and controls
    setupCamera();
    setupControls();
    setupLighting();

    // Build materials array
    materials = buildMaterialArray();

    // Load model
    try {
      await this._loadModel();
    } catch (error) {
      console.error('[InfinityViewer] Model load failed:', error);
    }

    // Apply initial configuration
    const initialConfig = options.initialConfig
      || (window.InfinityColors && window.InfinityColors.defaultConfiguration)
      || {};
    this.setAllColors(initialConfig);

    // Start render loop
    animate();

    // Setup visibility observer
    setupVisibilityObserver();

    // Setup click-to-select raycasting
    setupClickToSelect();

    // Handle window resize
    window.addEventListener('resize', this._onResize);

    // Dispatch loaded event
    canvas.dispatchEvent(new CustomEvent('infinity-viewer:loaded', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('infinity-viewer:loaded'));
  },

  /**
   * Load model based on URL extension.
   * @private
   */
  async _loadModel() {
    const urlPath = modelUrl.split('?')[0];
    const isJSON = urlPath.endsWith('.json') || urlPath.endsWith('.js');

    if (isJSON) {
      // Legacy Three.js JSON format
      const response = await fetch(modelUrl);
      let text = await response.text();
      // Strip Shopify CDN comment prefix (/** ... */) if present
      text = text.replace(/^\/\*[\s\S]*?\*\/\s*/, '');
      const json = JSON.parse(text);
      containerMesh = parseLegacyJSON(json);
      containerMesh.name = 'infinity-container';
      scene.add(containerMesh);
    } else {
      // GLB/GLTF format (primary path)
      const model = await loadGLBModel(modelUrl);
      model.name = 'infinity-container';
      containerMesh = model;
      scene.add(model);
    }

    // Center and scale the model
    this._normalizeModel();
  },

  /**
   * Center the model and scale it to fit the view.
   * @private
   */
  _normalizeModel() {
    if (!containerMesh) return;

    const box = new THREE.Box3().setFromObject(containerMesh);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    // Center the model
    containerMesh.position.sub(center);

    // Scale to a normalized size (fit within ~2 units)
    const maxDim = Math.max(size.x, size.y, size.z);
    if (maxDim > 0) {
      const scale = 2.5 / maxDim;
      containerMesh.scale.setScalar(scale);
    }

    // Offset slightly so the container is visually centered
    containerMesh.position.y += 0.2;
  },

  /**
   * Resize handler (bound).
   * @private
   */
  _onResize() {
    InfinityViewer.resize();
  },

  /**
   * Set color for a single panel zone.
   * @param {string} zoneName - Zone key (e.g., 'container_panel_1_color')
   * @param {number|string} colorHex - Hex value, hex string, or color name
   */
  setPanelColor(zoneName, colorHex) {
    applyZoneColor(zoneName, colorHex);
  },

  setAllColors(configObject) {
    if (!configObject) return;

    const patternKeys = [
      'design_party_stripe_color', 'design_party_pinstripe_1_color',
      'design_party_pinstripe_2_color', 'design_xout_color'
    ];

    for (const [zoneName, colorValue] of Object.entries(configObject)) {
      if (patternKeys.includes(zoneName)) continue;
      if (ZONE_MATERIAL_MAP[zoneName]) {
        applyZoneColor(zoneName, colorValue);
      }
    }

    // Enforce standard pattern visibility after bulk color set
    applyPattern('standard', { panel1Color: configObject.container_panel_1_color || 'Red' });
  },

  /**
   * Switch pattern and apply relevant colors.
   * @param {string} pattern - 'standard', 'partyStripe', or 'xout'
   * @param {object} colors - Pattern-specific color values
   */
  setPattern(pattern, colors) {
    applyPattern(pattern, colors);
  },

  /**
   * Set an embroidery zone color.
   * @param {string} zoneKey - e.g. 'embroidery_1flap_symbol_fill'
   * @param {string} colorInput - Color name or hex
   */
  setEmbroideryColor(zoneKey, colorInput) {
    applyEmbroideryColor(zoneKey, colorInput);
  },

  /**
   * Set all embroidery colors from a config object.
   * @param {object} embConfig - Map of { zoneKey: colorValue }
   */
  setAllEmbroideryColors(embConfig) {
    if (!embConfig) return;
    for (const [zoneKey, colorValue] of Object.entries(embConfig)) {
      if (EMBROIDERY_ZONE_MAP[zoneKey]) {
        applyEmbroideryColor(zoneKey, colorValue);
      }
    }
  },

  /**
   * Show neutral embroidery preview geometry without changing embroidery config.
   */
  showEmbroideryPlaceholders() {
    showEmbroideryPlaceholders();
  },

  highlightZone(zoneName) {
    startHighlight(zoneName);
  },

  /**
   * Remove all highlighting.
   */
  clearHighlight() {
    clearHighlightState();
  },

  /**
   * Get the zone key at a given client coordinate (for external raycasting).
   * @param {number} clientX
   * @param {number} clientY
   * @returns {string|null}
   */
  getZoneAtPoint(clientX, clientY) {
    return raycastZone(clientX, clientY);
  },

  /**
   * Set backpad material type.
   * @param {string} type - 'cordura' or 'mesh'
   */
  setBackpadMaterial(type) {
    const zoneInfo = ZONE_MATERIAL_MAP.container_backpad_color;
    if (!zoneInfo) return;

    for (const idx of zoneInfo.indices) {
      if (!materials[idx]) continue;

      if (type === 'mesh') {
        materials[idx].color.setHex(0xffffff);
        materials[idx].map = loadMeshBaseTexture();
        materials[idx].bumpMap = loadMeshBumpTexture();
        materials[idx].bumpScale = 0.4;
        materials[idx].roughness = 0.95;
        materials[idx].metalness = 0.0;
      } else {
        materials[idx].color.setHex(0x000000);
        materials[idx].map = loadCorduraBaseTexture();
        materials[idx].bumpMap = loadCorduraBumpTexture();
        materials[idx].bumpScale = 0.3;
      }
      materials[idx].userData.type = type === 'mesh' ? 'mesh' : 'cordura';
      materials[idx].needsUpdate = true;
    }
  },

  /**
   * Set hardware type (stainless steel or black).
   * @param {string} type - 'stainless' or 'black'
   */
  setHardwareType(type) {
    const hwMat = createHardwareMaterial(type);
    hwMat.userData = { type: 'hardware' };

    for (const idx of HARDWARE_MATERIAL_INDICES) {
      if (materials[idx]) {
        materials[idx].dispose();
      }
      materials[idx] = hwMat;
    }

    // If using GLB model, traverse and update hardware meshes
    if (containerMesh && !containerMesh.isMesh) {
      containerMesh.traverse((child) => {
        if (child.isMesh && child.material && child.material.userData &&
            child.material.userData.type === 'hardware') {
          child.material = hwMat;
        }
      });
    }

    // Update the mesh's material reference if using legacy format
    if (containerMesh && containerMesh.isMesh) {
      containerMesh.material = materials;
    }
  },

  /**
   * Handle container/window resize.
   */
  resize() {
    if (!canvas || !renderer || !camera) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    if (width === 0 || height === 0) return;

    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    renderer.setSize(width, height, false);
  },

  /**
   * Clean up all resources.
   */
  dispose() {
    // Stop animation
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    // Remove observers
    if (intersectionObserver) {
      intersectionObserver.disconnect();
      intersectionObserver = null;
    }

    // Remove resize listener
    window.removeEventListener('resize', this._onResize);

    // Remove click-to-select listeners
    if (canvas) {
      canvas.removeEventListener('pointerdown', onPointerDown, { capture: true });
      canvas.removeEventListener('click', onClick);
      canvas.removeEventListener('pointermove', onPointerMove);
    }
    raycaster = null;
    reverseIndexMap = null;

    // Dispose controls
    if (controls) {
      controls.dispose();
      controls = null;
    }

    // Dispose materials
    for (const mat of materials) {
      if (mat) mat.dispose();
    }
    materials = [];

    // Dispose textures
    if (corduraBaseTexture) {
      corduraBaseTexture.dispose();
      corduraBaseTexture = null;
    }
    if (corduraBumpTexture) {
      corduraBumpTexture.dispose();
      corduraBumpTexture = null;
    }
    for (const key of Object.keys(camoTextures)) {
      camoTextures[key].dispose();
    }
    camoTextures = {};

    // Dispose geometry
    if (containerMesh) {
      containerMesh.traverse((child) => {
        if (child.isMesh && child.geometry) {
          child.geometry.dispose();
        }
      });
      scene.remove(containerMesh);
      containerMesh = null;
    }

    // Dispose renderer
    if (renderer) {
      renderer.dispose();
      renderer = null;
    }

    // Clear references
    scene = null;
    camera = null;
    canvas = null;
  },

  /**
   * Get current state of all panel colors.
   * @returns {object} Map of zoneName to hex color value
   */
  getCurrentColors() {
    const result = {};
    for (const [zoneName, zoneInfo] of Object.entries(ZONE_MATERIAL_MAP)) {
      const idx = zoneInfo.indices[0];
      if (materials[idx]) {
        result[zoneName] = materials[idx].visible
          ? '#' + materials[idx].color.getHexString()
          : null;
      }
    }
    return result;
  },

  /**
   * Check if the viewer has been initialized.
   * @returns {boolean}
   */
  isInitialized() {
    return renderer !== null && scene !== null;
  }
};

/* ═══════════════════════════════════════════════════════════════════════════════
   AUTO-INITIALIZATION
   If the canvas element exists on page load, auto-initialize.
   ═══════════════════════════════════════════════════════════════════════════════ */

function autoInit() {
  const viewerCanvas = document.getElementById('infinity-viewer-canvas');
  if (viewerCanvas) {
    InfinityViewer.init(viewerCanvas);
  }
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInit);
} else {
  autoInit();
}

/* ═══════════════════════════════════════════════════════════════════════════════
   EXPOSE GLOBALLY
   ═══════════════════════════════════════════════════════════════════════════════ */

window.InfinityViewer = InfinityViewer;

export default InfinityViewer;
